import { useIsMounted } from "@whitespace/whitespace-utils";
import { API } from "aws-amplify";
// eslint-disable-next-line import/no-extraneous-dependencies
import { Method } from "axios";
import { useReducer, useCallback } from "react";
import useConfig from "./useConfig";

export interface IState {
  sending: boolean,
  data?: any,
  errors?: string[],
  statusCode : number
}

const initialState: IState = {
  sending: false, data: null, errors: undefined, statusCode: 0
};

type Action =
    | { type: "send" }
    | { type: "response", results: any, statusCode: number }
    | { type: "error", errors: string[] | undefined, statusCode: number }
    | { type: "reset" };

const reducer = (state: IState, action: Action): IState => {
  switch (action.type) {
    case "send":
      return {
        ...state,
        sending: true
      };
    case "response":
      return {
        ...initialState,
        sending: false,
        data: action.results,
        statusCode: action.statusCode
      };
    case "error":
      return {
        ...state,
        sending: false,
        errors: action.errors,
        statusCode: action.statusCode
      };
    case "reset":
      return initialState;
    default:
      return initialState;
  }
};

const useAmplifyREST = () => {
  const isMounted = useIsMounted();
  const [state, dispatch] = useReducer(reducer, initialState);

  const resetState = useCallback(() => {
    dispatch({ type: "reset" });
  }, []);

  const extractStrings = (obj : any, errors: string[]) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const k in obj) {
      if (typeof obj[k] === "object") {
        extractStrings(obj[k], errors);
      // eslint-disable-next-line no-constant-condition
      } else if (typeof [obj[k] === "string"]) errors.push(obj[k]);
    }
  };

  const get = async (endpointName : string, path: string, headers : any) => {
    const init = {
      headers,
      response: true
    };
    return API.get(endpointName, path, init);
  };

  const put = async (endpointName : string, path: string, headers : any, data: any) => {
    const init = {
      headers,
      body: data,
      response: true
    };
    return API.put(endpointName, path, init);
  };
  const post = async (endpointName : string, path: string, headers : any, data: any) => {
    const init = {
      headers,
      body: data,
      response: true
    };
    return API.post(endpointName, path, init);
  };

  const del = async (endpointName : string, path: string, headers : any, data: any) => {
    const init = {
      headers,
      body: data,
      response: true
    };
    return API.del(endpointName, path, init);
  };

  const patch = async (endpointName : string, path: string, headers : any, data: any) => {
    const init = {
      headers,
      body: data,
      response: true
    };
    return API.patch(endpointName, path, init);
  };

  const getCallMethod = (methodName: Method, endpointName: string, path: string, data: any, headers: any) : Promise<any> | undefined => {
    switch (methodName.toLocaleLowerCase()) {
      case ("get"): {
        return get(endpointName, path, headers);
      }
      case ("post"): {
        return post(endpointName, path, headers, data);
      }
      case ("put"): {
        return put(endpointName, path, headers, data);
      }
      case ("delete"): {
        return del(endpointName, path, headers, data);
      }
      case ("patch"): {
        return patch(endpointName, path, headers, data);
      }
      default: {
        return undefined;
      }
    }
  };

  const sendRequest = useCallback(async (
    path: string,
    method: Method,
    data?: any,
    headers: any = {},
    endpointName: string = useConfig().defaultEndpointName!
  ) => {
    const errorList : string[] = [];
    const apiCall = getCallMethod(method, endpointName, path, data, headers);
    if (!apiCall) {
      errorList.push(`method ${method} not supported`);
      dispatch({
        type: "error",
        errors: errorList,
        statusCode: 999
      });
      return;
    }
    dispatch({ type: "send" });
    await apiCall!
      .then(response => {
        if (isMounted()) {
          dispatch({ type: "response", results: response.data, statusCode: response.status });
        }
      })
      .catch((error: any) => {
        if (error.response) {
          if (!error.response?.data?.detail
            && !error.response?.data.errors
            && !error.response?.data.errorMessage) {
            errorList.push("There has been a problem with your request");
          } else {
            if (error.response?.data?.detail) {
              errorList.push(error.response?.data?.detail);
            }
            if (error.response?.data.errors) {
              extractStrings(error.response!.data.errors, errorList);
            }
            if (error.response?.data.errorMessage) {
              errorList.push(error.response?.data?.errorMessage);
            }
          }

          switch (error.response.status) {
            case 400:
              if (isMounted()) {
                dispatch({
                  type: "error",
                  errors: errorList,
                  statusCode: error.response.status
                });
              }
              break;
            case 401:
              if (isMounted()) {
                dispatch({
                  type: "error",
                  errors: undefined,
                  statusCode: error.response.status
                });
              }
              break;
            case 404:
              if (isMounted()) {
                errorList.push("Route does not exist");
                dispatch({
                  type: "error",
                  errors: errorList,
                  statusCode: error.response.status
                });
              }
              break;
            case 500:
              if (isMounted()) {
                dispatch({
                  type: "error",
                  errors: errorList,
                  statusCode: error.response.status
                });
              }
              break;
            default:
              if (isMounted()) {
                errorList.push(`Unexpected server error ${error.response.status}`);
                dispatch({
                  type: "error",
                  errors: errorList,
                  statusCode: error.response.status
                });
              }
              break;
          }
        } else if (isMounted()) {
          errorList.push("The service is temporarily unavailable");
          dispatch({
            type: "error",
            errors: errorList,
            statusCode: 500
          });
        }
      });
  }, [isMounted]);

  return {
    isSending: state.sending,
    data: state.data,
    errors: state.errors,
    statusCode: state.statusCode,
    resetState,
    sendRequest
  };
};

export { useAmplifyREST };
